import * as React from 'react';
import { Card, CardBody, Split, SplitItem, Switch, Title } from '@patternfly/react-core';
import UserModel from '@app/Model/UserModel';
import UserAuthentication from '@app/Security/UserAuthentication';
import QcSmartNotificationSettingInterface from '@app/Interface/QcSmartNotificationSettingsInterface';
import QcSmartNotificationSettingModel from '@app/Model/QcSmartNotificationSettingModel';
import { UserContext } from '@app/Context/UserContext';

interface NotificationOptInsProps {
    currentUser: UserModel;
}

const NotificationOptIns: React.FC<NotificationOptInsProps> = ({ currentUser }) => {

    const { userDispatch } = React.useContext(UserContext);

    const [canViewQcOptin, setCanViewQcOption] = React.useState(false);

    const [canViewSmartOptin, setCanViewSmartOptin] = React.useState(false);

    const defaultSetting = new QcSmartNotificationSettingModel();

    defaultSetting.fpu = currentUser.currentFpuId;

    const [qcSmartSetting, setQcSmartSetting] = React.useState<QcSmartNotificationSettingInterface>(defaultSetting);
    
    const handleChange = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {

        const fieldId = event.currentTarget.id;

        const updatedSetting = {...qcSmartSetting};

        const updatedUser = {...currentUser};

        updatedSetting.fpu = currentUser.currentFpuId;

        // update the form settings 
        if (fieldId === "enableQcNotifications") {
            updatedSetting.enable_qc_notification = checked;
        } else {
            updatedSetting.enable_smart_notification = checked;
        }

        setQcSmartSetting(updatedSetting);

        // update the local user
        // get a reference to original user setting
        if (updatedUser.qcSmartNotificationSettings && typeof updatedUser.qcSmartNotificationSettings !== "boolean") {

            let found = false;

            updatedUser.qcSmartNotificationSettings.forEach( (setting, index) => {

                if (setting.fpu === updatedUser.currentFpuId) {
                    updatedUser.qcSmartNotificationSettings[index] = updatedSetting;
                    found = true;
                }

            });

            if (!found) {

                // we have to add a new array
                updatedUser.qcSmartNotificationSettings.push(updatedSetting);
            }

        } else {

            // make a new array
            const updatedSettings: QcSmartNotificationSettingInterface[] = [];

            updatedSettings.push(updatedSetting);

            updatedUser.qcSmartNotificationSettings = updatedSettings;

        }

        // update user context
        userDispatch({
            type: "UPDATE",
            payload: updatedUser
        });
 
        // save updates to middleware
        UserAuthentication.saveUserQcSmartSettingsNonAsync(updatedUser.id, updatedSetting);
 
    }

    React.useEffect(() => {

        if (currentUser.currentFpuId) {

            setCanViewQcOption(UserAuthentication.userCanReceiveQcNotification(currentUser.viewPermissions, currentUser.currentFpuId));

            setCanViewSmartOptin(UserAuthentication.userCanReceiveSmartNotification(currentUser.viewPermissions, currentUser.currentFpuId));

            if ( currentUser.qcSmartNotificationSettings && typeof currentUser.qcSmartNotificationSettings != "boolean") {
                setQcSmartSetting(UserAuthentication.getQcSmartSettingByFpu(currentUser.qcSmartNotificationSettings, currentUser.currentFpuId));
            }

        }

    }, [currentUser.currentFpuId, currentUser.qcSmartNotificationSettings, currentUser.viewPermissions]);

    if (canViewQcOptin || canViewSmartOptin) {

        return (
            <>
                <Title headingLevel="h2">Notification Opt-Ins</Title>

                {canViewQcOptin &&
                    <Card className="pf-u-mb-md">
                        <CardBody>
                            <Split>
                                <SplitItem isFilled>
                                    <div>Send me <strong>Quality & Components</strong> notification</div>
                                    <div>(delivered every Wednesday morning)</div>
                                </SplitItem>
                                <SplitItem>
                                    <Switch
                                        id="enableQcNotifications"
                                        aria-label="Send Quality & Components Notification"
                                        isChecked={qcSmartSetting.enable_qc_notification}
                                        isReversed
                                        onChange={handleChange}
                                    />
                                </SplitItem>
                            </Split>
                        </CardBody>
                    </Card>
                }

                {canViewSmartOptin &&
                    <Card className="pf-u-mb-md">
                        <CardBody>
                            <Split>
                                <SplitItem isFilled>
                                    <div>Send me <strong>Smart Test Results</strong> notification</div>
                                </SplitItem>
                                <SplitItem>
                                    <Switch
                                        id="enableSmartTestResultsNotifications"
                                        aria-label="Send Smart Test Resutls Notification"
                                        isChecked={qcSmartSetting.enable_smart_notification}
                                        isReversed
                                        onChange={handleChange}
                                    />
                                </SplitItem>
                            </Split>
                        </CardBody>
                    </Card>
                }

            </>
        );

    }

    return null;

}

export default NotificationOptIns;